import React, { useState, useEffect } from 'react';
import { bomAPI, projectsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import {
  Plus,
  Package,
  Send,
  CheckCircle,
  Trash2,
  MessageCircle,
  Mail,
  Eye,
  Edit,
  FileText,
  Image,
  Download,
  FileSpreadsheet,
  Link2,
  Search
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const BOM_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  sent_to_client: 'bg-blue-100 text-blue-800',
  received: 'bg-green-100 text-green-800'
};

const BOMManager = ({ projects = [], onDataChange }) => {
  const { toast } = useToast();
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bomSearch, setBomSearch] = useState('');
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAddMaterialsDialog, setShowAddMaterialsDialog] = useState(false);
  const [selectedBOM, setSelectedBOM] = useState(null);
  
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [materials, setMaterials] = useState([{ material: '', quantity: '', unit: 'NOS' }]);
  const [notes, setNotes] = useState('');
  const [newMaterials, setNewMaterials] = useState([{ material: '', quantity: '', unit: 'NOS' }]);

  useEffect(() => {
    fetchBOMs();
  }, []);

  const fetchBOMs = async () => {
    try {
      const data = await bomAPI.getAll();
      setBoms(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch BOMs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBOM = async () => {
    if (!selectedProjectId) {
      toast({ title: "Error", description: "Please select a project", variant: "destructive" });
      return;
    }
    
    const validMaterials = materials.filter(m => m.material.trim());
    if (validMaterials.length === 0) {
      toast({ title: "Error", description: "Add at least one material", variant: "destructive" });
      return;
    }
    
    try {
      await bomAPI.create({
        project_id: selectedProjectId,
        materials: validMaterials.map(m => ({
          material: m.material,
          quantity: parseFloat(m.quantity) || 0,
          unit: m.unit
        })),
        notes
      });
      
      toast({ title: "Success", description: "BOM created successfully" });
      setShowCreateDialog(false);
      resetForm();
      fetchBOMs();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create BOM", variant: "destructive" });
    }
  };

  const handleSendToClient = async (bom) => {
    try {
      const result = await bomAPI.sendToClient(bom.id);
      toast({ title: "Success", description: result.message });
      fetchBOMs();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send BOM", variant: "destructive" });
    }
  };

  const handleMarkReceived = async (bom) => {
    try {
      const result = await bomAPI.markReceived(bom.id);
      toast({ title: "Success", description: result.message });
      fetchBOMs();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast({ title: "Error", description: "Failed to mark as received", variant: "destructive" });
    }
  };

  const handleAddMaterials = async () => {
    if (!selectedBOM) return;
    
    const validMaterials = newMaterials.filter(m => m.material.trim());
    if (validMaterials.length === 0) {
      toast({ title: "Error", description: "Add at least one material", variant: "destructive" });
      return;
    }
    
    try {
      await bomAPI.addMaterials(selectedBOM.id, validMaterials.map(m => ({
        material: m.material,
        quantity: parseFloat(m.quantity) || 0,
        unit: m.unit
      })));
      
      toast({ title: "Success", description: "Materials added to BOM" });
      setShowAddMaterialsDialog(false);
      setNewMaterials([{ material: '', quantity: '', unit: 'NOS' }]);
      fetchBOMs();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add materials", variant: "destructive" });
    }
  };

  const handleDeleteBOM = async (bomId) => {
    if (!window.confirm('Are you sure you want to delete this BOM?')) return;
    
    try {
      await bomAPI.delete(bomId);
      toast({ title: "Success", description: "BOM deleted" });
      fetchBOMs();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete BOM", variant: "destructive" });
    }
  };

  const handleExportPDF = async (bom) => {
    try {
      const blob = await bomAPI.exportPDF(bom.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BOM_${bom.bom_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "PDF downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export PDF", variant: "destructive" });
    }
  };

  const handleExportExcel = async (bom) => {
    try {
      const blob = await bomAPI.exportExcel(bom.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BOM_${bom.bom_number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "Excel downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export Excel", variant: "destructive" });
    }
  };

  const handleShareWhatsApp = async (bom) => {
    try {
      toast({ title: "Generating PDF...", description: "Please wait" });
      const result = await bomAPI.shareWhatsApp(bom.id);
      
      if (result.whatsapp_url) {
        // Open WhatsApp with the message
        window.open(result.whatsapp_url, '_blank');
        toast({ 
          title: "WhatsApp Opened", 
          description: "PDF link included in message. Send to share!" 
        });
      } else {
        toast({ title: "Error", description: "Could not generate WhatsApp link", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to share via WhatsApp", variant: "destructive" });
    }
  };

  const handleCopyLink = async (bom) => {
    try {
      toast({ title: "Generating PDF...", description: "Please wait" });
      const result = await bomAPI.shareWhatsApp(bom.id);
      
      if (result.download_url) {
        const fullUrl = `${window.location.origin}${result.download_url}`;
        
        // Try clipboard API first, fallback to execCommand
        try {
          await navigator.clipboard.writeText(fullUrl);
        } catch (clipboardError) {
          // Fallback for older browsers or when clipboard API fails
          const textArea = document.createElement('textarea');
          textArea.value = fullUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        
        toast({ 
          title: "Link Copied!", 
          description: "PDF download link copied to clipboard" 
        });
      } else {
        toast({ title: "Error", description: "Could not generate download link", variant: "destructive" });
      }
    } catch (error) {
      console.error('Copy link error:', error);
      toast({ title: "Error", description: "Failed to copy link", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setSelectedProjectId('');
    setMaterials([{ material: '', quantity: '', unit: 'NOS' }]);
    setNotes('');
  };

  const addMaterialRow = () => {
    setMaterials([...materials, { material: '', quantity: '', unit: 'NOS' }]);
  };

  const removeMaterialRow = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    setMaterials(updated);
  };

  const addNewMaterialRow = () => {
    setNewMaterials([...newMaterials, { material: '', quantity: '', unit: 'NOS' }]);
  };

  const updateNewMaterial = (index, field, value) => {
    const updated = [...newMaterials];
    updated[index] = { ...updated[index], [field]: value };
    setNewMaterials(updated);
  };

  const getWhatsAppLink = (bom) => {
    const project = projects.find(p => p.id === bom.project_id);
    let message = `*MATERIAL LIST*\n`;
    message += `Client: ${bom.client_name}\n`;
    message += `Project: ${project?.job_card_number || project?.project_number || ''}\n`;
    message += `BOM: ${bom.bom_number}\n\n`;
    message += `*Materials Required:*\n`;
    bom.materials.forEach((m, i) => {
      message += `${i+1}. ${m.material} - ${m.quantity} ${m.unit}\n`;
    });
    if (bom.notes) {
      message += `\nNotes: ${bom.notes}`;
    }
    return `https://wa.me/${bom.client_mobile}?text=${encodeURIComponent(message)}`;
  };

  const getEmailLink = (bom) => {
    const project = projects.find(p => p.id === bom.project_id);
    const subject = `Material List - ${bom.bom_number}`;
    let body = `MATERIAL LIST\n\n`;
    body += `Client: ${bom.client_name}\n`;
    body += `Project: ${project?.job_card_number || project?.project_number || ''}\n`;
    body += `BOM: ${bom.bom_number}\n\n`;
    body += `Materials Required:\n`;
    bom.materials.forEach((m, i) => {
      body += `${i+1}. ${m.material} - ${m.quantity} ${m.unit}\n`;
    });
    if (bom.notes) {
      body += `\nNotes: ${bom.notes}`;
    }
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return <div className="p-4 text-center">Loading BOMs...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Package className="w-5 h-5" />
          Bill of Materials
        </h2>
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by Job Card, client..."
              value={bomSearch}
              onChange={(e) => setBomSearch(e.target.value)}
              className="pl-9"
              data-testid="bom-search"
            />
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-bom-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create BOM
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {(() => {
          const filteredBoms = bomSearch.trim()
            ? boms.filter(bom => {
                const project = projects.find(p => p.id === bom.project_id);
                const jcNum = project?.job_card_number || project?.project_number || '';
                const q = bomSearch.toLowerCase();
                return (
                  jcNum.toLowerCase().includes(q) ||
                  (bom.client_name || '').toLowerCase().includes(q) ||
                  (bom.bom_number || '').toLowerCase().includes(q)
                );
              })
            : boms;

          return filteredBoms.length > 0 ? (
          filteredBoms.map(bom => {
            const project = projects.find(p => p.id === bom.project_id);
            return (
              <Card key={bom.id} data-testid={`bom-card-${bom.bom_number}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{bom.bom_number}</h3>
                        <Badge className={BOM_STATUS_COLORS[bom.status]}>
                          {bom.status?.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{bom.client_name}</p>
                      <p className="text-xs text-gray-500">
                        JC: {project?.job_card_number || project?.project_number || 'N/A'} | {bom.materials?.length || 0} materials
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedBOM(bom); setShowViewDialog(true); }} title="View BOM">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportPDF(bom)} title="Download PDF" data-testid={`download-pdf-${bom.bom_number}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportExcel(bom)} title="Download Excel" data-testid={`download-excel-${bom.bom_number}`}>
                        <FileSpreadsheet className="w-4 h-4" />
                      </Button>
                      
                      {bom.status === 'draft' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { setSelectedBOM(bom); setShowAddMaterialsDialog(true); }} title="Add Materials">
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-green-600 border-green-300 hover:bg-green-50" onClick={() => handleShareWhatsApp(bom)} title="Send PDF via WhatsApp" data-testid={`whatsapp-bom-${bom.bom_number}`}>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </Button>
                          <Button variant="outline" size="sm" className="text-purple-600 border-purple-300 hover:bg-purple-50" onClick={() => handleCopyLink(bom)} title="Copy PDF Link" data-testid={`copy-link-bom-${bom.bom_number}`}>
                            <Link2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => handleSendToClient(bom)} data-testid={`send-bom-${bom.bom_number}`}>
                            <Send className="w-4 h-4 mr-1" /> Mark Sent
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteBOM(bom.id)} title="Delete BOM">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      {bom.status === 'sent_to_client' && (
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => handleMarkReceived(bom)} data-testid={`receive-bom-${bom.bom_number}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark as Received
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">{bomSearch ? `No BOMs found for "${bomSearch}"` : 'No BOMs created yet'}</p>
            </CardContent>
          </Card>
        );
        })()}
      </div>

      {/* Create BOM Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Bill of Materials</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Job Card *</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => !['completed', 'invoiced'].includes(p.status)).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.job_card_number || p.project_number} - {p.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Materials *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addMaterialRow}>
                  <Plus className="w-4 h-4 mr-1" /> Add Row
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Material</th>
                      <th className="p-2 text-left w-24">Quantity</th>
                      <th className="p-2 text-left w-24">Unit</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {materials.map((mat, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-1">
                          <Input value={mat.material} onChange={(e) => updateMaterial(idx, 'material', e.target.value)} placeholder="e.g., 16MM PLY" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input type="number" value={mat.quantity} onChange={(e) => updateMaterial(idx, 'quantity', e.target.value)} placeholder="0" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Select value={mat.unit} onValueChange={(v) => updateMaterial(idx, 'unit', v)}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NOS">NOS</SelectItem>
                              <SelectItem value="MTRS">MTRS</SelectItem>
                              <SelectItem value="LENGTH">LENGTH</SelectItem>
                              <SelectItem value="SHEET">SHEET</SelectItem>
                              <SelectItem value="KG">KG</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          {materials.length > 1 && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeMaterialRow(idx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateBOM} data-testid="confirm-create-bom">Create BOM</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View BOM Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bill of Materials - {selectedBOM?.bom_number}</DialogTitle>
          </DialogHeader>
          
          {selectedBOM && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">Client</p>
                  <p className="font-medium">{selectedBOM.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={BOM_STATUS_COLORS[selectedBOM.status]}>
                    {selectedBOM.status?.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Materials ({selectedBOM.materials?.length || 0})</h4>
                <table className="w-full text-sm border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 text-left w-10">#</th>
                      <th className="border p-2 text-left">Material</th>
                      <th className="border p-2 text-right w-24">Quantity</th>
                      <th className="border p-2 text-center w-20">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBOM.materials?.map((mat, idx) => (
                      <tr key={mat.id || idx}>
                        <td className="border p-2 text-gray-500">{idx + 1}</td>
                        <td className="border p-2 font-medium">{mat.material}</td>
                        <td className="border p-2 text-right">{mat.quantity}</td>
                        <td className="border p-2 text-center">{mat.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Invoice Photo Section */}
              {selectedBOM.invoice_photo_url && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg" data-testid="bom-invoice-section">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Image className="w-4 h-4 text-blue-600" />
                    Invoice Photo
                  </h4>
                  <img 
                    src={`${API_BASE}${selectedBOM.invoice_photo_url}`}
                    alt="Invoice" 
                    className="max-h-64 rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => window.open(`${API_BASE}${selectedBOM.invoice_photo_url}`, '_blank')}
                    data-testid="bom-invoice-photo"
                  />
                  {selectedBOM.invoice_uploaded_by && (
                    <p className="text-xs text-gray-500 mt-2">
                      Uploaded by: {selectedBOM.invoice_uploaded_by}
                      {selectedBOM.invoice_uploaded_at && ` on ${new Date(selectedBOM.invoice_uploaded_at).toLocaleDateString()}`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button variant="outline" onClick={() => handleExportPDF(selectedBOM)} data-testid="dialog-download-pdf">
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" onClick={() => handleExportExcel(selectedBOM)} data-testid="dialog-download-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Materials Dialog */}
      <Dialog open={showAddMaterialsDialog} onOpenChange={setShowAddMaterialsDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Add Materials to {selectedBOM?.bom_number}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <Label>New Materials</Label>
              <Button type="button" variant="outline" size="sm" onClick={addNewMaterialRow}>
                <Plus className="w-4 h-4 mr-1" /> Add Row
              </Button>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Material</th>
                    <th className="p-2 text-left w-24">Quantity</th>
                    <th className="p-2 text-left w-24">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {newMaterials.map((mat, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-1">
                        <Input value={mat.material} onChange={(e) => updateNewMaterial(idx, 'material', e.target.value)} placeholder="e.g., EDGE BANDING" className="h-8" />
                      </td>
                      <td className="p-1">
                        <Input type="number" value={mat.quantity} onChange={(e) => updateNewMaterial(idx, 'quantity', e.target.value)} placeholder="0" className="h-8" />
                      </td>
                      <td className="p-1">
                        <Select value={mat.unit} onValueChange={(v) => updateNewMaterial(idx, 'unit', v)}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NOS">NOS</SelectItem>
                            <SelectItem value="MTRS">MTRS</SelectItem>
                            <SelectItem value="LENGTH">LENGTH</SelectItem>
                            <SelectItem value="SHEET">SHEET</SelectItem>
                            <SelectItem value="KG">KG</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMaterialsDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMaterials}>Add Materials</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BOMManager;
